import {
  withAskUserFreeText,
  withAskUserPick,
  type AskUserAnswer,
  type AskUserAnswers,
  type AskUserRequest,
} from '@littlebird/sdk';
import { makeAutoObservable, runInAction } from 'mobx';

type DraftAnswer = {
  selected: string[];
  freeText: string;
  skipped: boolean;
};

const emptyAnswer = (): DraftAnswer => ({
  selected: [],
  freeText: '',
  skipped: false,
});

class AskUserStore {
  private draftsByRequestId = new Map<string, Map<string, DraftAnswer>>();
  private submittingRequestIds = new Set<string>();
  // Answers sent from a card, kept on screen until sync moves the card out of draft
  private sentRequestIds = new Set<string>();

  constructor() {
    makeAutoObservable(this);
  }

  private draftFor(requestId: string): Map<string, DraftAnswer> {
    const existing = this.draftsByRequestId.get(requestId);
    if (existing) return existing;
    this.draftsByRequestId.set(requestId, new Map());
    return this.draftsByRequestId.get(requestId)!;
  }

  getAnswer(requestId: string, questionId: string): DraftAnswer | undefined {
    return this.draftsByRequestId.get(requestId)?.get(questionId);
  }

  answeredCount(requestId: string): number {
    return this.draftsByRequestId.get(requestId)?.size ?? 0;
  }

  hasAnyAnswer(requestId: string): boolean {
    return this.answeredCount(requestId) > 0;
  }

  isSubmitting(requestId: string): boolean {
    return this.submittingRequestIds.has(requestId);
  }

  beginSubmit(requestId: string): boolean {
    if (this.isSubmitting(requestId)) return false;
    runInAction(() => this.submittingRequestIds.add(requestId));
    return true;
  }

  endSubmit(requestId: string) {
    runInAction(() => this.submittingRequestIds.delete(requestId));
  }

  isSent(requestId: string): boolean {
    return this.sentRequestIds.has(requestId);
  }

  markSent(requestId: string) {
    runInAction(() => this.sentRequestIds.add(requestId));
  }

  unmarkSent(requestId: string) {
    runInAction(() => this.sentRequestIds.delete(requestId));
  }

  isAnswered(requestId: string, questionId: string): boolean {
    return this.draftsByRequestId.get(requestId)?.has(questionId) ?? false;
  }

  toggleOption(
    requestId: string,
    questionId: string,
    option: string,
    allowMultiple: boolean
  ) {
    runInAction(() => {
      const draft = this.draftFor(requestId);
      const answer = draft.get(questionId) ?? emptyAnswer();
      const { selected, freeText } = withAskUserPick(
        answer,
        option,
        allowMultiple
      );

      if (selected.length === 0 && !freeText) {
        draft.delete(questionId);
        return;
      }
      draft.set(questionId, {
        ...answer,
        selected: [...selected],
        freeText,
        skipped: false,
      });
    });
  }

  setFreeText(
    requestId: string,
    questionId: string,
    freeText: string,
    allowMultiple: boolean
  ) {
    runInAction(() => {
      const draft = this.draftFor(requestId);
      const answer = draft.get(questionId) ?? emptyAnswer();
      const { selected } = withAskUserFreeText(answer, freeText, allowMultiple);

      if (!freeText && selected.length === 0) {
        draft.delete(questionId);
        return;
      }
      draft.set(questionId, {
        ...answer,
        selected: [...selected],
        freeText,
        skipped: false,
      });
    });
  }

  skipQuestion(requestId: string, questionId: string) {
    runInAction(() => {
      this.draftFor(requestId).set(questionId, {
        ...emptyAnswer(),
        skipped: true,
      });
    });
  }

  unskipQuestion(requestId: string, questionId: string) {
    runInAction(() => {
      this.draftsByRequestId.get(requestId)?.delete(questionId);
    });
  }

  skipAll(request: AskUserRequest) {
    runInAction(() => {
      const draft = this.draftFor(request.request_id);
      request.questions.forEach((question) =>
        draft.set(question.id, { ...emptyAnswer(), skipped: true })
      );
    });
  }

  buildAnswers(request: AskUserRequest): AskUserAnswers | null {
    const draft = this.draftsByRequestId.get(request.request_id);
    if (!draft || draft.size === 0) return null;

    const items: AskUserAnswer[] = request.questions.map((question) => {
      const answer = draft.get(question.id);
      return {
        question_id: question.id,
        prompt: question.prompt,
        selected: answer?.selected ?? [],
        free_text: answer?.freeText || null,
        skipped: answer?.skipped ?? true,
      };
    });

    return { request_id: request.request_id, items };
  }

  clear(requestId: string) {
    runInAction(() => {
      this.draftsByRequestId.delete(requestId);
      this.sentRequestIds.delete(requestId);
    });
  }
}

export const askUserStore = new AskUserStore();
